import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import ResourceSelect from "../../../../../../components/shared/inputs/ResourceSelect";
import { Calendar } from "../../../../../../components/ui/calendar";
import { Field } from "../../../../../../components/shared/Form";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useDateFilters } from "../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { getUserSearchFilters } from "../../../../../People/useColumns";
import { renderUserName } from "../../../../../../utils/helpers/user";
import type { CopyValues } from "./types";

interface ScheduleProps {
    values: CopyValues | null;
    dates: { from: string | undefined, to: string | undefined };
    setDates: Dispatch<SetStateAction<{ from: string | undefined, to: string | undefined }>>;
    error: string | null;
    setError: Dispatch<SetStateAction<string | null>>;
}

function Schedule({ values, dates, setDates, error, setError }: ScheduleProps) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { mode, from, to } = useDateFilters();
    const { type } = useScheduleContext();
    const calendarMode = useMemo(() => {
        return values?.type === "cell" ? "range" : "single";
    }, [values?.type]);

    const selected = useMemo(() => ({
        from: dates?.from ? moment(dates.from).toDate() : undefined,
        to: dates?.to ? moment(dates.to).toDate() : undefined,
    }), [dates.from, dates.to]);

    const onSelect = useCallback((range: { from?: Date; to?: Date }, selectedDay?: Date) => {
        if (values?.type === "cell") {
            if (range.from && range.to) {
                const from = moment(range.from).format("YYYY-MM-DD");
                const to = moment(range.to).format("YYYY-MM-DD");
                setDates({ from, to });
                setError(null);
            }
        } else {
            const formattedDate = moment(selectedDay).format("YYYY-MM-DD");
            setDates({ from: formattedDate, to: formattedDate });
            setError(null);
        }
    }, [setDates, setError, values?.type]);

    return (
        <div className="tw-flex tw-justify-center tw-flex-col tw-w-full">
            {(type === "team" && values?.type && !["day", "bulk", "bulk-day"].includes(values?.type)) && (
                <>
                    <h3 className="tw-mb-3 tw-w-full tw-text-sm" >
                        {t(
                            "select-one-or-more-employees",
                            {
                                date: mode === "day"
                                    ? `date: ${moment(from).format(dateFormat)}`
                                    : `dates ${moment(from).format(dateFormat)} - ${moment(to).format(dateFormat)}`
                            }
                        )}
                    </h3>

                    <Field
                        name="users"
                        className="tw-w-full tw-px-5"
                    >
                        <ResourceSelect
                            resourcePath="/users"
                            renderLabel={renderUserName}
                            placeholder={t("users")}
                            mode="multiple"
                            showSearch
                            getSearchFilter={getUserSearchFilters}
                        />
                    </Field>
                </>
            )}

            <h3 className="tw-my-2 tw-text-center tw-w-full tw-text-sm tw-px-5">
                {(mode === "day" || values?.type === "day")
                    ? t("select-date-when-mode-date", {
                        date: moment(values?.type === "day"
                            ? values?.date
                            : from).format(dateFormat)
                    })
                    : t("select-date-to-copy", {
                        range: `${moment(from).format(dateFormat)} - ${moment(to).format(dateFormat)}:`
                    })
                }
            </h3>

            <div className="tw-w-full tw-flex tw-items-center tw-justify-center">
                <Calendar
                    numberOfMonths={1}
                    mode={calendarMode as any}
                    disabled={{ before: moment(to).add(1, "day").toDate() }}
                    onSelect={onSelect}
                    selected={selected}
                    defaultMonth={moment(to).add(1, "day").toDate()}
                    className="tw-[--cell-size:--spacing(9)] tw-p-4"
                />
            </div>
            <div className="tw-text-rose-500 tw-text-sm tw-text-start">
                {error}
            </div>
        </div>
    )
}

export default Schedule;
